import { Box } from "../../components/Box";
import { Paragraph } from "../../components/Paragraph";
import { AddressVerificationCheck } from "./AddressVerificationCheck";
import { AdverseMediaCheck } from "./AdverseMediaCheck";
import { AssociatedPartiesCheck } from "./AssociatedPartiesCheck";
import { BankDetailsCheck } from "./BankDetailsCheck";
import { EntityDetailsCheck } from "./EntityDetailsCheck";
import { GeoLocationCheck } from "./GeoLcationCheck";
import { IdentityLivenessCheck } from "./IdentityLivenessCheck";
import { IndividualChecksMap } from "./helper";
import { NatureOfBusinessCheck } from "./NatureOfBusinessCheck";
import { PepAndSanctionsCheck } from "./PepAndSanctionsCheck";
import { PersonalDetailsCheck } from "./PersonalDetailsCheck";
import { PreviousNamesCheck } from "./PreviousNamesCheck";
import { ProofOfAddressCheck } from "./ProofOfAddressCheck";
import { SanctionsCheck } from "./SanctionCheck";
import { ScreeningCheck } from "./ScreeningCheck";
import { SourceOfFundsCheck } from "./SourceOfFundsCheck";
import { AddressUpdateCheck } from "./AddressUpdateCheck";

type CheckDetailArgs = {
  name: string;
  id: number;
  type?: "individual" | "entity";
};

const noop = () => {};

/**
 * The check-name-to-component mapping, shared by the Screening record check
 * panel. Every component keeps its isOpen/onRequestClose/useSlider props from
 * its slider-driven origin (IndividualView, CheckSection) — passing isOpen
 * true with a no-op close here is accepted debt, not a fresh design choice.
 *
 * An unmapped name renders a stated fallback: a blank panel behind a tab reads
 * as broken, whereas the equivalent gap behind a slider's "View" button was at
 * least invisible.
 */
export const renderCheckDetail = ({ name, id, type }: CheckDetailArgs) => {
  if (name === IndividualChecksMap.address_verification_check.name) {
    return (
      <AddressVerificationCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
      />
    );
  }
  if (name === IndividualChecksMap.proof_of_address_check.name) {
    return (
      <ProofOfAddressCheck isOpen onRequestClose={noop} id={id} useSlider={false} />
    );
  }
  if (name === IndividualChecksMap["pep_&_sanctions_check"].name) {
    return (
      <PepAndSanctionsCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
      />
    );
  }
  if (name === IndividualChecksMap.bank_account_details_check.name) {
    return (
      <BankDetailsCheck isOpen onRequestClose={noop} id={id} useSlider={false} />
    );
  }
  if (name === IndividualChecksMap.personal_details_match_check.name) {
    return (
      <PersonalDetailsCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
      />
    );
  }
  if (name === IndividualChecksMap.entity_details_match_check.name) {
    return (
      <EntityDetailsCheck isOpen onRequestClose={noop} id={id} useSlider={false} />
    );
  }
  if (name === IndividualChecksMap.address_update_check.name) {
    return (
      <AddressUpdateCheck isOpen onRequestClose={noop} id={id} useSlider={false} />
    );
  }
  if (name === IndividualChecksMap.entity_previous_name_check.name) {
    return (
      <PreviousNamesCheck isOpen onRequestClose={noop} id={id} useSlider={false} />
    );
  }
  if (name === IndividualChecksMap.parties_check.name) {
    return (
      <AssociatedPartiesCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
      />
    );
  }
  if (name === IndividualChecksMap.nature_of_business_check.name) {
    return (
      <NatureOfBusinessCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
      />
    );
  }
  if (name === IndividualChecksMap["id_&_liveliness_check"].name) {
    return (
      <IdentityLivenessCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
      />
    );
  }
  if (name === IndividualChecksMap.geo_location_check.name) {
    return (
      <GeoLocationCheck isOpen onRequestClose={noop} id={id} useSlider={false} />
    );
  }
  if (
    name === IndividualChecksMap.adverse_media_check.name ||
    name === IndividualChecksMap.entity_adverse_media_check.name
  ) {
    return (
      <AdverseMediaCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
        type={type}
      />
    );
  }
  if (name === IndividualChecksMap.source_of_funds_check.name) {
    return (
      <SourceOfFundsCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
      />
    );
  }
  if (name === IndividualChecksMap.sanctions_check.name) {
    return (
      <SanctionsCheck isOpen onRequestClose={noop} id={id} useSlider={false} />
    );
  }
  if (
    name === IndividualChecksMap.ln_screening_check.name ||
    name === IndividualChecksMap.ln_screening_check_entity.name
  ) {
    return (
      <ScreeningCheck
        isOpen
        onRequestClose={noop}
        id={id}
        useSlider={false}
        type={
          name === IndividualChecksMap.ln_screening_check_entity.name
            ? "entity"
            : "individual"
        }
        permissionCodename="screening.review_activity"
        permissionDeniedMessage="You don't have permission to accept or reject this screening check"
      />
    );
  }

  return (
    <Box p={3}>
      <Paragraph mt={0} mb={0} color="gray.60">
        This check type isn&apos;t supported in this view yet.
      </Paragraph>
    </Box>
  );
};
